import React, { useId, useState } from "react";
import { api, errMsg, unwrap } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileImage, FileText, FileUp, X } from "lucide-react";
import { useI18n } from "@/i18n";

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_FILE_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
]);

const COPY = {
  en: {
    addFiles: "Add photo or PDF",
    attachments: "Attachments",
    fileLimit: "JPEG, PNG, or PDF. Up to 10 MB each, maximum 5 files.",
    invalidFile: "Choose JPEG, PNG, or PDF files only.",
    maxFiles: "An announcement can have up to 5 attachments.",
    oversizedFile: "Each attachment must be 10 MB or smaller.",
    remove: "Remove attachment",
    uploading: "Uploading...",
    view: "Open attachment",
  },
  ar: {
    addFiles: "إضافة صورة أو ملف PDF",
    attachments: "المرفقات",
    fileLimit: "JPEG أو PNG أو PDF. حتى 10 ميغابايت لكل ملف، وبحد أقصى 5 ملفات.",
    invalidFile: "اختر ملفات JPEG أو PNG أو PDF فقط.",
    maxFiles: "يمكن أن يحتوي الإعلان على خمسة مرفقات كحد أقصى.",
    oversizedFile: "يجب ألا يتجاوز حجم كل مرفق 10 ميغابايت.",
    remove: "إزالة المرفق",
    uploading: "جارٍ الرفع...",
    view: "فتح المرفق",
  },
};

const attachmentUrl = (fileId) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("eduos_token");
  const authQuery = token ? `?auth=${encodeURIComponent(token)}` : "";
  return `${backendUrl}/api/v1/files/${encodeURIComponent(fileId)}${authQuery}`;
};

const isImage = (attachment) =>
  attachment.contentType?.startsWith("image/")
  || /\.(jpe?g|png)$/i.test(attachment.name || "");

export const attachmentItemsFromRecord = (record) =>
  (record?.attachment_file_ids || []).map((id, index) => ({
    id,
    name: record.attachment_file_names?.[index] || `Attachment ${index + 1}`,
    contentType: record.attachment_content_types?.[index] || "",
  }));

export function AnnouncementAttachmentPicker({
  value = [],
  onChange,
  disabled = false,
  inputId,
}) {
  const { language } = useI18n();
  const copy = COPY[language] || COPY.en;
  const generatedId = useId().replace(/:/g, "");
  const id = inputId || `announcement-attachments-${generatedId}`;
  const attachments = Array.isArray(value) ? value : [];
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      toast.error(copy.maxFiles);
      return;
    }
    if (files.some((file) => !ALLOWED_FILE_TYPES.has(file.type))) {
      toast.error(copy.invalidFile);
      return;
    }
    if (files.some((file) => file.size > MAX_FILE_SIZE_BYTES)) {
      toast.error(copy.oversizedFile);
      return;
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const response = unwrap(
          await api.post("/files/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          }),
        );
        uploaded.push({
          id: response.id,
          name: file.name,
          contentType: response.content_type || file.type,
        });
      }
      onChange([...attachments, ...uploaded]);
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase font-semibold">{copy.attachments}</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={id}
          type="file"
          multiple
          accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
          className="sr-only"
          onChange={uploadFiles}
          disabled={disabled || uploading}
          data-testid={`${id}-input`}
        />
        <Label
          htmlFor={id}
          className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-[var(--accent)] bg-orange-50/40 px-3 text-sm font-medium text-[var(--accent)] disabled:pointer-events-none disabled:opacity-50"
        >
          <FileUp className="h-4 w-4" />
          {uploading ? copy.uploading : copy.addFiles}
        </Label>
        <span className="text-xs text-[var(--text-muted)]">{copy.fileLimit}</span>
      </div>
      {attachments.length > 0 && (
        <ul className="space-y-1.5">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="flex min-w-0 items-center gap-2 rounded-md border border-[var(--subtle)] px-2 py-1.5 text-sm"
            >
              {isImage(attachment) ? (
                <FileImage className="h-4 w-4 shrink-0 text-[var(--accent)]" />
              ) : (
                <FileText className="h-4 w-4 shrink-0 text-[var(--accent)]" />
              )}
              <span className="min-w-0 flex-1 truncate">{attachment.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => onChange(attachments.filter((item) => item.id !== attachment.id))}
                aria-label={`${copy.remove}: ${attachment.name}`}
                title={copy.remove}
                disabled={disabled || uploading}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AnnouncementAttachmentList({ record, className = "" }) {
  const { language } = useI18n();
  const copy = COPY[language] || COPY.en;
  const attachments = attachmentItemsFromRecord(record);

  if (!attachments.length) return null;

  return (
    <div className={`mt-3 flex flex-wrap gap-2 ${className}`}>
      {attachments.map((attachment) => (
        <a
          key={attachment.id}
          href={attachmentUrl(attachment.id)}
          target="_blank"
          rel="noreferrer"
          title={`${copy.view}: ${attachment.name}`}
          className="group flex max-w-full items-center gap-2 rounded-md border border-[var(--subtle)] bg-[var(--bg)] px-2 py-1.5 text-sm text-[var(--text-primary)] hover:border-[var(--accent)]"
          data-testid={`announcement-attachment-${attachment.id}`}
        >
          {isImage(attachment) ? (
            <img
              src={attachmentUrl(attachment.id)}
              alt={attachment.name}
              className="h-10 w-10 shrink-0 rounded object-cover"
            />
          ) : (
            <FileText className="h-5 w-5 shrink-0 text-[var(--accent)]" />
          )}
          <span className="max-w-48 truncate group-hover:text-[var(--accent)]">{attachment.name}</span>
        </a>
      ))}
    </div>
  );
}